import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import { formatEffort } from '../../../../shared/effort'
import { ticketTypeLabel } from '../../../../shared/helpers'
import useLatestOnly from '../../../../shared/useLatestOnly'

import Loading from '../../../../UI/Loading'

import styles from './stats.module.scss'

const MONTHS = [
	'Jänner',
	'Februar',
	'März',
	'April',
	'Mai',
	'Juni',
	'Juli',
	'August',
	'September',
	'Oktober',
	'November',
	'Dezember'
]

// „2026-10-26" → „26.10." — die Wochengrenzen kommen fertig vom Server, hier
// wird nur umgestellt, nicht gerechnet.
const shortDate = iso => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`

const monthLabel = row => `${MONTHS[row.month - 1]} ${row.year}`
const weekLabel = row => `KW ${row.week}/${row.year}`
const weekRange = row => `${shortDate(row.from)}–${shortDate(row.to)}${row.to.slice(0, 4)}`

/**
 * Auswertung der Entwicklungen: Anzahl und Aufwand je Monat und je
 * Kalenderwoche.
 *
 * Zwei Größen mit verschiedenem Maßstab — deshalb je eine eigene Balkenspalte
 * statt eines Diagramms mit zwei Achsen, an dem man die Länge der Balken nicht
 * mehr vergleichen kann. Jede Zeile trägt ihre Zahlen ausgeschrieben, die
 * Balken sind Lesehilfe, nicht die einzige Angabe.
 *
 * Suche und Filter kommen von der Seite: Wer nach einem Projekt filtert, sieht
 * hier genau dessen Aufwand.
 */
export default function ChangelogStats({ search, filterQuery, onFilter }) {
	const [stats, setStats] = useState(null)
	const latestOnly = useLatestOnly()

	useEffect(() => {
		const isCurrent = latestOnly()

		// `disableToast`: Der eigene Satz benennt, was nicht geladen werden konnte.
		http.get(TApi.ADMIN_CHANGELOG_STATS, {
			params: { search, filter: filterQuery },
			disableToast: true
		})
			.then(({ data }) => {
				if (isCurrent()) setStats(data)
			})
			.catch(error => {
				if (!isCurrent()) return
				notifyError(error, 'Die Auswertung konnte nicht geladen werden.')
				setStats({ total: { count: 0, minutes: 0, withoutEffort: 0 }, weeks: [], months: [] })
			})
	}, [search, filterQuery, latestOnly])

	if (!stats) return <Loading />

	const { total } = stats

	if (!total.count) {
		return (
			<div className={styles.empty}>
				Keine Entwicklungen in dieser Auswahl. Auslieferungen haben keinen Aufwand und zählen hier nicht.
			</div>
		)
	}

	const average = total.count - total.withoutEffort ? total.minutes / (total.count - total.withoutEffort) : null

	return (
		<div className={styles.root}>
			<div className={styles.tiles}>
				<Tile label="Entwicklungen" value={total.count.toLocaleString('de-AT')} />
				<Tile label="Aufwand gesamt" value={formatEffort(total.minutes)} />
				<Tile label="Ø je Entwicklung" value={average === null ? '—' : formatEffort(average)} />
				<Tile
					label="Ohne Aufwand"
					value={total.withoutEffort.toLocaleString('de-AT')}
					hint={total.withoutEffort ? 'nicht in den Summen' : null}
				/>
			</div>

			<p className={styles.note}>
				Gezählt werden nur Entwicklungen, keine Auslieferungen. Suche und Filter der Seite gelten auch hier.
				Wochen von Montag bis Sonntag, gerechnet in Wiener Zeit.
			</p>

			<PeriodTable title="Je Monat" rows={stats.months} label={monthLabel} firstColumn="Monat" />
			<PeriodTable
				title="Je Kalenderwoche"
				rows={stats.weeks}
				label={weekLabel}
				sublabel={weekRange}
				firstColumn="Kalenderwoche"
			/>

			{/*
			 * Je Art und je Projekt: größter Aufwand zuerst. Ein Klick auf eine
			 * Zeile schränkt die ganze Seite darauf ein — Kurzwahl auf dasselbe
			 * Filterfeld wie in der Leiste, kein zweiter Zustand daneben.
			 */}
			{stats.byType?.length > 0 && (
				<PeriodTable
					title="Je Art"
					rows={stats.byType}
					label={typeLabel}
					firstColumn="Art"
					newestFirst={false}
					onRowClick={
						onFilter && (row => onFilter('type', [{ value: row.key, label: typeLabel(row) }]))
					}
				/>
			)}
			{stats.byProject?.length > 0 && (
				<PeriodTable
					title="Je Projekt"
					rows={stats.byProject}
					label={projectLabel}
					firstColumn="Projekt"
					newestFirst={false}
					onRowClick={
						onFilter &&
						(row => onFilter('projectId', [{ value: row.key, label: projectLabel(row) }]))
					}
				/>
			)}
		</div>
	)
}

ChangelogStats.propTypes = {
	search: PropTypes.string,
	filterQuery: PropTypes.string,
	// (feld, auswahl) — setzt einen Filter der Seite.
	onFilter: PropTypes.func
}

ChangelogStats.defaultProps = {
	search: '',
	filterQuery: '{}',
	onFilter: null
}

const typeLabel = row => (row.key === 'none' ? 'Ohne Art' : ticketTypeLabel(row.key))
const projectLabel = row => row.title || 'Ohne Projekt'

function Tile({ label, value, hint }) {
	return (
		<div className={styles.tile}>
			<span className={styles.tileLabel}>{label}</span>
			<span className={styles.tileValue}>{value}</span>
			{hint && <span className={styles.tileHint}>{hint}</span>}
		</div>
	)
}

Tile.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
	hint: PropTypes.string
}

Tile.defaultProps = {
	hint: null
}

/**
 * Eine Zeitreihe als Tabelle, neueste Zeile oben — beim Abrechnen sucht man
 * den letzten Monat, nicht den ersten. Leere Zeiträume bleiben stehen: Wer
 * eine Reihe von Balken liest, sieht sonst nicht, dass eine Woche fehlt.
 */
function PeriodTable({ title, rows, label, sublabel, firstColumn, newestFirst, onRowClick }) {
	const maxCount = Math.max(1, ...rows.map(row => row.count))
	const maxMinutes = Math.max(1, ...rows.map(row => row.minutes))
	const ordered = newestFirst ? [...rows].reverse() : rows

	return (
		<section className={styles.section} aria-label={title}>
			<h3 className={styles.sectionTitle}>{title}</h3>
			<table className={styles.table}>
				<thead>
					<tr>
						<th scope="col">{firstColumn}</th>
						<th scope="col">Entwicklungen</th>
						<th scope="col">Aufwand</th>
					</tr>
				</thead>
				<tbody>
					{ordered.map(row => (
						<tr key={row.key} className={row.count ? undefined : styles.rowEmpty}>
							<th scope="row" className={styles.period}>
								{onRowClick ? (
									<button
										type="button"
										className={styles.periodButton}
										title="Seite darauf einschränken"
										onClick={() => onRowClick(row)}
									>
										{label(row)}
									</button>
								) : (
									<span className={styles.periodLabel}>{label(row)}</span>
								)}
								{sublabel && <span className={styles.periodRange}>{sublabel(row)}</span>}
							</th>
							<td>
								<Bar
									share={row.count / maxCount}
									text={row.count.toLocaleString('de-AT')}
									title={`${label(row)}: ${row.count} Entwicklungen`}
								/>
							</td>
							<td>
								<Bar
									share={row.minutes / maxMinutes}
									text={formatEffort(row.minutes)}
									title={`${label(row)}: ${formatEffort(row.minutes)}${
										row.withoutEffort ? ` (${row.withoutEffort} ohne Aufwand)` : ''
									}`}
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</section>
	)
}

PeriodTable.propTypes = {
	title: PropTypes.string.isRequired,
	rows: PropTypes.array.isRequired,
	label: PropTypes.func.isRequired,
	sublabel: PropTypes.func,
	firstColumn: PropTypes.string.isRequired,
	newestFirst: PropTypes.bool,
	onRowClick: PropTypes.func
}

PeriodTable.defaultProps = {
	sublabel: null,
	newestFirst: true,
	onRowClick: null
}

function Bar({ share, text, title }) {
	return (
		<div className={styles.bar} title={title}>
			<span className={styles.barTrack}>
				{share > 0 && <span className={styles.barFill} style={{ width: `${share * 100}%` }} />}
			</span>
			<span className={styles.barText}>{text}</span>
		</div>
	)
}

Bar.propTypes = {
	share: PropTypes.number.isRequired,
	text: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired
}
